import Clipboard from 'clipboard'

export type Response = 'success' | 'error';

export interface ICopyTextEvent {
  action: string
  text: string
  trigger: Element
  clearSelection(): void
}

export interface IClipboardConfig {
  autoSetContainer?: boolean
  appendToBody?: boolean
}

export function useClipboardV2() {
  const execCommandCopy = (text: string) => {
    return new Promise<void>((resolve, reject) => {
      const fakeElement = document.createElement('textarea')
      fakeElement.value = text
      fakeElement.style.opacity = '0'
      fakeElement.style.pointerEvents = 'none' // Ensure it doesn't interfere with user interaction
      fakeElement.style.whiteSpace = 'pre'

      document.body.appendChild(fakeElement)
      if (!fakeElement.hasAttribute('readonly')) {
        fakeElement.setAttribute('readonly', '')
        fakeElement.select()
        fakeElement.setSelectionRange(0, fakeElement.value.length)
      }

      try {
        const success = document.execCommand('copy')
        document.body.removeChild(fakeElement)
        success ? resolve() : reject(new DOMException('The request is not allowed', 'NotAllowedError'))
      } catch (err) {
        console.error('execCommandCopy error:', err)
        fakeElement.removeAttribute('readonly')
        document.body.removeChild(fakeElement)
        reject(err)
      }
    })
  }

  const clipboardCopy = async (
    text: string,
    container: any,
    callback: (event: ICopyTextEvent, status: Response) => void,
    config?: IClipboardConfig,
    selector?: string | Element | NodeListOf<Element>
  ) => {
    const VueClipboardConfig = {
      autoSetContainer: config?.autoSetContainer ?? true,
      appendToBody: config?.appendToBody ?? true
    }

    try {
      await execCommandCopy(text)
      // eslint-disable-next-line standard/no-callback-literal
      callback(
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        { action: 'copy', text, trigger: container, clearSelection: () => { } },
        'success'
      )
      return
    } catch (error) {
      console.log('execCommandCopy 失败，尝试使用 navigator.clipboard')
    }

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
        // eslint-disable-next-line standard/no-callback-literal
        callback(
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          { action: 'copy', text, trigger: container, clearSelection: () => { } } as ICopyTextEvent,
          'success'
        )
        return
      }
    } catch (error) {
      console.log('navigator.clipboard.writeText 失败，尝试使用 Clipboard.js 和 execCommandCopy')
    }

    const fakeElement = document.createElement('button')
    const selectorElement = VueClipboardConfig.autoSetContainer ? fakeElement : selector
    const clipboard = new Clipboard(selectorElement as Element, {
      text: () => text,
      action: () => 'copy',
      container: container !== undefined ? container : document.body
    })

    clipboard.on('success', (e: ICopyTextEvent) => {
      clipboard.destroy()
      callback(e, 'success')
    })

    clipboard.on('error', async (e: ICopyTextEvent) => {
      clipboard.destroy()
      callback(e, 'error')
    })

    if (!VueClipboardConfig.autoSetContainer) return
    if (VueClipboardConfig.appendToBody) document.body.appendChild(fakeElement)
    fakeElement.click()
    if (VueClipboardConfig.appendToBody) document.body.removeChild(fakeElement)
  }

  return { clipboardCopy }
}
